import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BankTypeEnum,
  Box,
  ButtonResponsiveWrapper,
  ErrorModal,
  FlexControlledForm,
  Preloader,
  useResponsive,
} from '@common-fe/common-fe';
import styled from 'styled-components';
import * as yup from 'yup';

import AppButton from '@/components/controls/AppButton';
import SaveAsDefaultCheckbox from '@/modules/transaction/components/ReimburseMe/ConfirmationStep/Immediately/SaveAsDefaultCheckbox';
import theme from '@/styles/theme';

import {
  DEBIT_CARD_TEST_ID_PREFIX,
  DebitCardFields,
  DebitCardPayload,
  useDebitCardFields,
} from './hooks/useDebitCardFields';
import { useAddPaymentDebitCardQuery } from './queries/useAddPaymentDebitCard.query';

const BUTTON_WIDTH = '150px';

const StyledBox = styled(Box)`
  .small-input {
    width: 100px;
    div[data-testid=_${DEBIT_CARD_TEST_ID_PREFIX}_${DebitCardFields.expirationDate}_mask-text-input_error-message] {
      white-space: nowrap;
    }
  }
`;

interface Props {
  onBack?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
  editableMethodId?: string;
  allowSaveAsDefault?: boolean;
}

export const DebitCardForm: React.FC<Props> = ({
  onBack,
  onSuccess,
  editableMethodId,
  allowSaveAsDefault,
  onError,
}) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const [serverErrorText, setServerErrorText] = useState('');
  const [formValues, setFormValues] = useState<DebitCardPayload>();
  const [isSaveAsDefault, setIsSaveAsDefault] = useState(false);
  const { fields, bankCardType } = useDebitCardFields(formValues);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { addDebitCard, updateDebitCard } = useAddPaymentDebitCardQuery(onSuccess);

  const activeValues = useMemo(() => {
    try {
      const validatorMap = fields.reduce(
        (map, field) => ({
          ...map,
          [field.name]: field.validator,
        }),
        {}
      );
      const schema = yup.object().shape(validatorMap);
      schema.validateSync(formValues, { abortEarly: false });
      return true;
    } catch {
      return false;
    }
  }, [fields, formValues]);

  const handleChangeValues = useCallback((values: DebitCardPayload) => {
    setFormValues((prev) => ({ ...prev, ...values }));
  }, []);

  const handleSubmit = useCallback(async () => {
    try {
      setSubmitted(true);
      const cardType = Object.keys(bankCardType).find((type) => bankCardType[type as BankTypeEnum]);
      if (activeValues && onSuccess && cardType && formValues) {
        setLoading(true);

        if (editableMethodId) {
          await updateDebitCard({ ...formValues, cardType }, editableMethodId);
        } else {
          await addDebitCard({ ...formValues, cardType });
        }
        onSuccess();
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      // @ts-ignore
      if (onError && e.response.status === 409) {
        onError();
      } else {
        setServerErrorText(
          'We couldn’t add your credit card at the moment due to a server error. Please try again later.'
        );
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeValues, formValues, onError, onSuccess, addDebitCard, bankCardType, isSaveAsDefault]);

  return (
    <>
      <Box pad={isMobile ? 'spacing8' : 'spacing24'} round="moduleRound" background="module">
        <StyledBox round="container1Round" background="canvas">
          <FlexControlledForm
            fields={fields}
            showError={submitted}
            editMode
            formStyle={isMobile ? { margin: theme.spacings.spacing12 } : undefined}
            onChangeValues={handleChangeValues}
          />
        </StyledBox>
      </Box>
      <Box
        pad={{ top: 'medium' }}
        direction={isMobile ? 'column' : 'row'}
        align={isMobile ? 'start' : 'center'}
      >
        {allowSaveAsDefault && (
          <Box pad={isMobile ? { horizontal: 'spacing8' } : {}}>
            <SaveAsDefaultCheckbox
              isSaveAsDefault={isSaveAsDefault}
              setIsSaveAsDefault={setIsSaveAsDefault}
            />
          </Box>
        )}
        <ButtonResponsiveWrapper
          fill={isMobile ? true : undefined}
          align="center"
          direction={isMobile ? 'column' : 'row'}
          margin={isMobile ? {} : { left: 'auto' }}
        >
          <AppButton
            testId="DebitCard_form_back"
            type="button"
            buttonType="secondary"
            onClick={onBack}
            width={isMobile ? '100%' : BUTTON_WIDTH}
          >
            {editableMethodId ? t('Cancel') : t('Back')}
          </AppButton>
          <AppButton
            testId="DebitCard_form_submit"
            type="button"
            width={isMobile ? '100%' : BUTTON_WIDTH}
            onClick={handleSubmit}
          >
            {loading ? <Preloader color="white" /> : t('Submit')}
          </AppButton>
        </ButtonResponsiveWrapper>
      </Box>
      {serverErrorText ? (
        <ErrorModal
          header="Error!"
          helpText={serverErrorText}
          buttonText="Close!"
          visible
          onSetVisible={() => setServerErrorText('')}
        />
      ) : null}
    </>
  );
};
